import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import styles from './RegisterFormGanheMaisCorretor.module.css';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/forms/Input';
import { Notify, NotifyUpdate } from '../../../../components/Notify';
import * as routes from '../../../../constants/Routes';
import { useAuth } from '../../../../hooks/useAuth';
import { api } from '../../../../server/api';
import { maskTEL } from '../../../../utils/mask';
import { getSearchParams } from '../../../../utils/getSearchParams';
import { decodeSafedata } from '../../../../utils/decodeSafeData';

interface FormValues {
  name: string;
  lastname: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  creci: string;
  workSegment: string;
}

const WORK_SEGMENTS = [
  { label: 'Médio e Alto Padrão', value: 'medioAltopadrao' },
  { label: 'MCMV', value: 'mcmv' },
  { label: 'Todos', value: 'todos' },
];

export function RegisterFormGanheMaisCorretor() {
  const { client, signIn } = useAuth();
  const location = useLocation();
  const isIframeRegisterRoute = location.pathname.includes('/iframe-register');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();

  const handleForm = handleSubmit(async (formData) => {
    if (formData.password !== formData.confirmPassword) {
      return Notify('Confirme as senhas', 'warn');
    }

    let actionMgm: any = {};
    const action = getSearchParams('action');
    const clientId = getSearchParams('client');
    const playerId = getSearchParams('player');

    if (action && clientId) {
      actionMgm = {
        ...(playerId && { playerId }),
        actionId: action,
        clientId: clientId,
      };
    }

    const nId = Notify('Criando sua conta...', 'loading');

    try {
      const parameters = {
        actionMgm,
        player: {
          ...formData,
          name: `${formData.name} ${formData.lastname}`,
          clientId: client.id,
          hostId: actionMgm.playerId ?? null,
          regional: 'sim',
        },
        targetPublic: client.targetPublic,
        client: client,
        page: 'register',
      };

      const { data } = await api.post('/createPlayer', parameters);

      if (data.data.status) {
        if (data.data.status === 'error/user-exists-other-client') {
          NotifyUpdate(
            'Este e-mail já está cadastrado na plataforma.',
            'error',
            nId
          );
        } else {
          NotifyUpdate(
            'Tivemos um problema ao criar a conta, tente novamente.',
            'error',
            nId
          );
        }
        return;
      }

      await signIn({
        email: formData.email,
        password: formData.password,
        clientId: client.id,
      });

      NotifyUpdate('Conta criada com sucesso!', 'success', nId);

      if (isIframeRegisterRoute) {
        // Ver comentário equivalente em pages/Register/index.tsx: o
        // localStorage gravado aqui dentro do iframe não é visível pra aba
        // nova (storage particionado por site de terceiros), então passamos
        // o token pela URL pra aba nova re-autenticar sozinha.
        let accessToken = '';
        let refreshToken = '';
        try {
          const rawPlayerAuth = localStorage.getItem('@pwa:playerAuth');
          if (rawPlayerAuth) {
            const decoded = decodeSafedata(rawPlayerAuth);
            const parsedPlayerAuth = JSON.parse(JSON.parse(decoded));
            accessToken = parsedPlayerAuth?.stsTokenManager?.accessToken || '';
            refreshToken =
              parsedPlayerAuth?.stsTokenManager?.refreshToken || '';
          }
        } catch (error) {
          console.log('Error reading playerAuth for iframe handoff: ', error);
        }

        const homeUrl =
          `${window.location.origin}${routes.HOME}?fromIframeRegister=true` +
          `&iat=${encodeURIComponent(accessToken)}` +
          `&irt=${encodeURIComponent(refreshToken)}` +
          `&iclient=${encodeURIComponent(client.id)}` +
          `&ihost=${encodeURIComponent(actionMgm?.playerId || '')}`;

        const newWindow = window.open(homeUrl, '_blank');
        if (!newWindow) {
          Notify('Por favor, permita pop-ups e tente novamente', 'warn');
        }
      }
    } catch (error) {
      console.log('RegisterFormGanheMaisCorretor handleForm error', error);
      NotifyUpdate(
        'Tivemos um problema para validar os dados cadastrados, tente novamente!',
        'error',
        nId
      );
    }
  });

  return (
    <form onSubmit={handleForm}>
      <div className="mb-2">
        <Input
          type="text"
          placeholder="Nome"
          {...register('name', { required: true })}
          errors={errors.name ? 'Campo obrigatório' : ''}
        />
      </div>
      <div className="mb-2">
        <Input
          type="text"
          placeholder="Sobrenome"
          {...register('lastname', { required: true })}
          errors={errors.lastname ? 'Campo obrigatório' : ''}
        />
      </div>
      <div className="mb-2">
        <Input
          type="email"
          placeholder="E-mail"
          {...register('email', { required: true })}
          errors={errors.email ? 'Campo obrigatório' : ''}
        />
      </div>
      <div className="mb-2">
        <Input
          type="text"
          placeholder="Celular / WhatsApp"
          {...register('phoneNumber', {
            required: true,
            onChange(event) {
              event.target.value = maskTEL(event.target.value);
            },
          })}
          maxLength={15}
          errors={errors.phoneNumber ? 'Campo obrigatório' : ''}
        />
      </div>
      <div className="mb-2">
        <Input
          type="password"
          placeholder="Senha"
          {...register('password', { required: true })}
          errors={errors.password ? 'Campo obrigatório' : ''}
        />
      </div>
      <div className="mb-2">
        <Input
          type="password"
          placeholder="Confirmar senha"
          {...register('confirmPassword', { required: true })}
          errors={errors.confirmPassword ? 'Campo obrigatório' : ''}
        />
      </div>
      <div className="mb-2">
        <Input
          type="text"
          placeholder="CRECI (ex: SP-123456)"
          {...register('creci', { required: true })}
          errors={errors.creci ? 'Campo obrigatório' : ''}
        />
      </div>
      <div className="mb-2">
        <select
          className={styles.select}
          {...register('workSegment', { required: true })}
        >
          <option value="">Qual segmento você atua?</option>
          {WORK_SEGMENTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        {errors.workSegment && (
          <span className={styles.error}>Campo obrigatório</span>
        )}
      </div>
      <div className="mb-2">
        <span className={styles.terms}>
          Ao clicar em <strong>Cadastrar</strong>, você concorda com as{' '}
          <a
            target="_blank"
            href="https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPoliticaPrivacidade_GanheMaisCorretor_CORRETOR.docx.pdf?alt=media&token=0d3cc869-b87e-4c60-b611-b4313265034d"
          >
            Política de Privacidade
          </a>{' '}
          e{' '}
          <a href={`/_/${client?.clientUrl}`} target="_blank">
            Termos de uso
          </a>{' '}
          do programa.
        </span>
      </div>
      <div className="d-grid">
        <Button
          icon="check"
          text={isSubmitting ? 'Cadastrando' : 'Cadastrar'}
          type="submit"
          color="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting}
        />
      </div>
    </form>
  );
}
